import type { Holiday } from "./holiday-api";
import { TONE_CLASSES, type Tone } from "./tokens";

export interface CalendarEvent {
  readonly date: string; // YYYY-MM-DD
  readonly label: string;
  readonly tone?: Tone; // 省略時は shinonome（従来色）
}

export interface CalendarProps {
  readonly year: number;
  readonly month: number; // 0-11
  readonly holidays?: readonly Holiday[];
  readonly events?: readonly CalendarEvent[];
  readonly onNavigate: (year: number, month: number) => void;
}

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

function pad(n: number): string {
  return n < 10 ? "0" + n : String(n);
}
function ymd(y: number, m: number, d: number): string {
  return y + "-" + pad(m + 1) + "-" + pad(d);
}
function monthLabel(y: number, m: number): string {
  return y + "年" + (m + 1) + "月";
}

export function createCalendar(props: CalendarProps): HTMLElement {
  const root = document.createElement("div");
  const holidayMap = new Map((props.holidays ?? []).map((h) => [h.date, h.name]));
  const eventMap = new Map<string, CalendarEvent[]>();
  for (const e of props.events ?? []) {
    const arr = eventMap.get(e.date) ?? [];
    arr.push(e);
    eventMap.set(e.date, arr);
  }

  const header = document.createElement("div");
  header.className = "mb-2 flex items-center justify-between";
  const prev = document.createElement("button");
  prev.type = "button";
  prev.className = "rounded border border-slate-300 px-2 py-1 text-sm hover:bg-slate-100";
  prev.textContent = "前月";
  prev.setAttribute("aria-label", "前の月");
  prev.addEventListener("click", () => {
    const m = props.month - 1;
    if (m < 0) props.onNavigate(props.year - 1, 11);
    else props.onNavigate(props.year, m);
  });
  const next = document.createElement("button");
  next.type = "button";
  next.className = "rounded border border-slate-300 px-2 py-1 text-sm hover:bg-slate-100";
  next.textContent = "翌月";
  next.setAttribute("aria-label", "次の月");
  next.addEventListener("click", () => {
    const m = props.month + 1;
    if (m > 11) props.onNavigate(props.year + 1, 0);
    else props.onNavigate(props.year, m);
  });
  const label = document.createElement("h3");
  label.className = "text-base font-medium text-slate-900";
  label.dataset["testid"] = "calendar-label";
  label.textContent = monthLabel(props.year, props.month);
  header.append(prev, label, next);

  const table = document.createElement("table");
  table.className = "w-full table-fixed border-collapse text-sm";
  const caption = document.createElement("caption");
  caption.className = "sr-only";
  caption.textContent = monthLabel(props.year, props.month) + " のスケジュールカレンダー";
  const thead = document.createElement("thead");
  const hr = document.createElement("tr");
  WEEKDAYS.forEach((w, i) => {
    const th = document.createElement("th");
    th.scope = "col";
    th.className = "border border-slate-200 py-1 text-center font-medium " + (i === 0 ? "text-red-700" : i === 6 ? "text-blue-700" : "text-slate-600");
    th.textContent = w;
    hr.append(th);
  });
  thead.append(hr);

  const tbody = document.createElement("tbody");
  const firstDow = new Date(Date.UTC(props.year, props.month, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(props.year, props.month + 1, 0)).getUTCDate();
  let day = 1 - firstDow;
  for (let week = 0; week < 6 && day <= daysInMonth; week += 1) {
    const tr = document.createElement("tr");
    for (let dow = 0; dow < 7; dow += 1, day += 1) {
      const td = document.createElement("td");
      td.className = "h-16 border border-slate-100 align-top p-1";
      if (day >= 1 && day <= daysInMonth) {
        const dateStr = ymd(props.year, props.month, day);
        const holiday = holidayMap.get(dateStr);
        const evs = eventMap.get(dateStr);
        const dnum = document.createElement("div");
        dnum.className = "text-xs " + (holiday !== undefined || dow === 0 ? "text-red-700" : dow === 6 ? "text-blue-700" : "text-slate-700");
        dnum.textContent = String(day);
        td.append(dnum);
        if (holiday !== undefined) {
          td.dataset["testid"] = "holiday";
          const h = document.createElement("div");
          h.className = "truncate text-[10px] text-red-700";
          h.textContent = holiday;
          td.append(h);
        }
        if (evs !== undefined) {
          td.dataset["event"] = "1";
          for (const ev of evs) {
            const e = document.createElement("div");
            const toneChip = ev.tone !== undefined ? TONE_CLASSES[ev.tone].chip : "bg-shinonome-50 text-shinonome-700";
            e.className = "mt-0.5 truncate rounded px-1 text-[10px] " + toneChip;
            e.textContent = ev.label;
            td.append(e);
          }
        }
      } else {
        td.className += " bg-slate-50";
      }
      tr.append(td);
    }
    tbody.append(tr);
  }

  table.append(caption, thead, tbody);
  root.append(header, table);
  return root;
}
